import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../components/AppContext'; // Canvi per utilitzar el hook personalitzat
import config from '../config';
import {
    Button, 
    CircularProgress, 
    Typography, 
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Chip,
    Tooltip,
    useTheme // Importem useTheme per accedir a la paleta de colors del tema
} from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

// Helper per renderitzar el Chip d'estat de sincronització
const renderStatusChip = (status, errorMessage) => {
    if (!status) return null;

    if (status === 'OK') {
        return <Chip label="OK" color="success" size="small" />;
    }

    if (status === 'ERROR') {
        const chip = (
            <Chip
                label="ERROR"
                color="error"
                size="small"
                icon={errorMessage ? <ErrorOutline fontSize="small" /> : undefined}
            />
        );
        return errorMessage ? (
            <Tooltip title={errorMessage} arrow placement="top">
                <Box component="span" sx={{ display: 'inline-flex' }}>
                    {chip}
                </Box>
            </Tooltip>
        ) : chip;
    }

    if (status === 'PARCIAL') {
        const chip = <Chip label="PARCIAL" color="warning" size="small" />;
        return errorMessage ? (
            <Tooltip title={errorMessage} arrow placement="top">
                <Box component="span" sx={{ display: 'inline-flex' }}>
                    {chip}
                </Box>
            </Tooltip>
        ) : chip;
    }

    return <Chip label={status} size="small" variant="outlined" />;
};

// Helper para formatear la fecha a YYYY-MM-DD
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Helper para obtener la fecha de hace N días
const getDateMinusDays = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
};

const SincroBancaria = () => {
  const theme = useTheme(); // Obtenim l'objecte theme
  const [processId, setProcessId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loadingProcess, setLoadingProcess] = useState(false); // Renombrado para claridad
  const { ctxUser, setSincroExecucionsData } = useAppContext(); // Obtenim setSincroExecucionsData del context
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
  const { userapp: paramUserapp } = useParams();
  const userapp = paramUserapp;
  const navigate = useNavigate();

  // --- Estados y lógica para la sección de resultados diarios ---
  const [selectedDate, setSelectedDate] = useState(formatDate(getDateMinusDays(7))); // <-- Cambio aquí: Inicializar 7 días antes
  const [syncResult, setSyncResult] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [errorResults, setErrorResults] = useState(null);

  const startProcess = async () => {
    try {
      setLoadingProcess(true);
      setProcessId(null); // Resetea el ID por si acaso
      const response = await axios.get(`${config.apiBaseUrl}/v1/syncbanc/${userapp}`, {
        headers: { Authorization: `${jwtToken}`}
      });
      setProcessId(response.data);
      setProcessing(true);
      // No ponemos setLoadingProcess(false) aquí, porque el useEffect se encargará
    } catch (error) {
      console.error("Error al iniciar el proceso", error);
      // TODO: Mostrar un mensaje de error al usuario
      setLoadingProcess(false);
      setProcessing(false);
    }
  };

  // --- Función para buscar resultados diarios (extraída para reutilización) ---
  const fetchSyncResults = useCallback(async () => {
      if (!selectedDate || !userapp || !jwtToken) return;

      setLoadingResults(true);
      setErrorResults(null);
      // No limpiamos syncResult aquí para evitar parpadeo si ya hay datos
      // setSyncResult(null);

      try {
          const response = await axios.get(`${config.apiBaseUrl}/v1/resumexecucio/list/${userapp}`, {
              params: {
                startDia: selectedDate
              },
              headers: { Authorization: `${jwtToken}` }
          });

          const data = response.data || [];

          // Crear una copia del array y ordenarla por itimestamp descendente
          const sortedData = [...data].sort((a, b) => {
              if (a.itimestamp < b.itimestamp) {
                  return 1; // b es más reciente, b va antes
              }
              if (a.itimestamp > b.itimestamp) {
                  return -1; // a es más reciente, a va antes
              }
              return 0; // Son iguales
          });

          // Actualiza el estado con la lista ordenada
          setSyncResult(sortedData);
          setSincroExecucionsData(sortedData); // Emmagatzemem les dades al context
      } catch (err) {
          console.error("Error fetching sync results:", err);
          if (err.response && err.response.status === 404) {
              // 404 significa que no hi ha historial de sincronitzacions, la qual cosa és normal per a un nou compte.
              setErrorResults(null);
          } else {
              setErrorResults("Error al cargar los resultados de la sincronización.");
          }
          setSyncResult([]); // Poner array vacío en caso de error
          setSincroExecucionsData([]); // Netejar dades del context en cas d'error
      } finally {
          setLoadingResults(false);
      }
  }, [selectedDate, userapp, jwtToken, setSincroExecucionsData]); // Afegim setSincroExecucionsData a les dependències

  useEffect(() => {
    let interval;
    if (processId && processing) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${config.apiBaseUrl}/v1/syncbanc/isinproces/${processId}`, {
            headers: { Authorization: `${jwtToken}`}
          });
          const isProcesing = response.data===true;
          setProcessing(isProcesing);
          if (!isProcesing) {
            clearInterval(interval); // Limpiamos el intervalo aquí cuando termina
            setLoadingProcess(false); // Proceso terminado
            // Añadimos un pequeño delay antes de intentar refrescar los resultados
            // para dar tiempo al backend a actualizarse.
            // Ajusta el tiempo (en ms) si es necesario. 1000ms = 1 segundo.
            setTimeout(() => fetchSyncResults(), 1500); // Espera 1.5 segundos
          }
        } catch (error) {
          console.error("Error al consultar el estado del proceso", error);
          clearInterval(interval); // Aseguramos limpiar en caso de error
          setLoadingProcess(false); // Error al consultar, detenemos carga
        }
      }, 5000); // Intervalo de 10 segundos
    }

    return () => clearInterval(interval); // Limpia el intervalo cuando el componente se desmonte o cambie el estado
  }, [processId, processing, jwtToken, fetchSyncResults]); // <-- Quitamos fetchSyncResults de aquí

  // --- useEffect para buscar resultados cuando cambia la fecha, usuario o token ---
  // --- O cuando el proceso de sincronización termina ---
  useEffect(() => {
    // Ejecutamos la carga inicial o cuando cambia la fecha/usuario/token
    // Esto cubre la carga inicial, los cambios de fecha, y lo más importante:
    // se ejecutará cuando 'processing' cambie de true a false.
    // La actualización DESPUÉS de que el proceso termina ahora se maneja
    // con el setTimeout dentro del useEffect del intervalo.
    if (!processing && !processId) { // Carga inicial o si no hay proceso activo
      fetchSyncResults();
    }
  }, [fetchSyncResults, processing, processId]); // <-- Añadimos 'processing' como dependencia

  const handleDateChange = (event) => {
      setSelectedDate(event.target.value);
  };

  const handleNavigateToDetail = (executionId, connectorId = null) => {
    if (connectorId) {
      navigate(`/${userapp}/sincro/detall/${executionId}/${connectorId}`);
    } else {
      navigate(`/${userapp}/sincro/detall/${executionId}`);
    }
  };

  // --- Fin de la sección de resultados diarios ---
  return (
    <Box sx={{ padding: 3 }}>
      {/* Sección de Inicio y Estado del Proceso */}
      <Paper sx={{ padding: 3, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>Procés Sincronització Bancaria</Typography>
        {processId &&
          <Box sx={{ marginBottom: 2 }}>
            <Typography>ID del procés: {processId}</Typography>
            <Typography>Estat: { processing ? 'EN CURS' : 'FINALITZAT' }</Typography>
            {loadingProcess && <CircularProgress size={24} sx={{ marginLeft: 2, verticalAlign: 'middle' }} />}
          </Box>
        }
        {/* Mostrar botón solo si no hay un proceso activo o cargando */}
        { !processing && !loadingProcess &&
          <Button variant="contained" onClick={startProcess} disabled={loadingProcess}>
            Iniciar Procés
          </Button>
        }
        {/* Indicar si se está iniciando el proceso */}
        { loadingProcess && !processId && <Typography sx={{ fontStyle: 'italic', marginTop: 1 }}>Iniciant procés...</Typography>}
      </Paper>

      {/* Nueva Sección: Resultados de Sincronización Diaria */}
      <Paper sx={{ padding: 3, overflow: 'hidden' }}>
          <Typography variant="h5" gutterBottom>
              Resultats Diaris
          </Typography>

          <TextField
              label="Seleccionar Data"
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              InputLabelProps={{
                  shrink: true,
              }}
              sx={{ marginBottom: 3, minWidth: '180px' }}
              disabled={loadingResults} // Deshabilitar mientras carga
          />

          {loadingResults && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
          {errorResults && <Alert severity="error" sx={{ marginBottom: 2 }}>{errorResults}</Alert>}

          {!loadingResults && !errorResults && syncResult && syncResult.length > 0 && (
              <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="taula resultats sincronització">
                      <TableHead sx={{ backgroundColor: 'lightgray' }}>
                          <TableRow>
                              <TableCell>Data procés</TableCell>
                              <TableCell>Connector ID</TableCell>
                              <TableCell align="center">Estat</TableCell>
                              <TableCell align="right">Num Registrats</TableCell>
                              <TableCell align="right">Num Tractats</TableCell>
                              <TableCell align="right">Num Filtrats Dates</TableCell>
                              <TableCell align="right">Num Filtrats Repes</TableCell>
                              <TableCell align="right">Num Filtrats Config</TableCell>
                              <TableCell align="right">Num Concepte Assignat</TableCell>
                          </TableRow>
                      </TableHead>
                      <TableBody>
                          {syncResult.map((resultado , indexR) => (
                            resultado.connectorResults.map((connector, index) => (
                                <TableRow
                                  key={`${connector.connectorId}-${indexR}-${index}`}
                                  hover // Afegeix efecte hover per defecte de MUI
                                  onClick={() => handleNavigateToDetail(resultado.id, connector.connectorId)}
                                  sx={{ 
                                    cursor: 'pointer', 
                                    '&:hover': { 
                                      backgroundColor: theme.palette.action.hover // Efecte hover per a la fila
                                      // Les cel·les filles que no tinguin un backgroundColor propi definit
                                      // o que siguin transparents, mostraran aquest color.
                                    }
                                  }}
                                >
                                    {index === 0 && (
                                      <TableCell 
                                        onClick={(e) => {
                                          e.stopPropagation(); // Evita que l'onClick de la TableRow s'activi
                                          handleNavigateToDetail(resultado.id);
                                        }}
                                        rowSpan={resultado.connectorResults.length}
                                        sx={{
                                          cursor: 'pointer',
                                          // Definim un fons base explícit per a aquesta cel·la.
                                          // Això evita que hereti el backgroundColor del hover de la TableRow pare.
                                          backgroundColor: theme.palette.background.paper, 
                                          '&:hover': { 
                                            backgroundColor: theme.palette.action.hover // Hover específic per a la cel·la de data
                                          },
                                          verticalAlign: 'top'
                                        }}
                                       >
                                         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                           <Typography variant="body2">
                                             {new Date(resultado.itimestamp).toLocaleString()}
                                           </Typography>
                                           {resultado.status && (
                                             <Box sx={{ display: 'inline-flex' }}>
                                               {renderStatusChip(resultado.status, resultado.errorMessage)}
                                             </Box>
                                           )}
                                         </Box>
                                       </TableCell>
                                    )}
                                    <TableCell component="th" scope="row"> {/* Ja no necessita onClick ni sx per ser clicable */}
                                      {connector.connectorId}
                                    </TableCell>
                                    <TableCell align="center">
                                      {renderStatusChip(connector.status, connector.errorMessage)}
                                    </TableCell>
                                    <TableCell align="right">{connector.numeroMovimientosRegistrados}</TableCell>
                                    <TableCell align="right">{connector.numMovimentsTractats}</TableCell>
                                    <TableCell align="right">{connector.numMovimentsFiltratsEntreDates}</TableCell>
                                    <TableCell align="right">{connector.numMovimentsFiltratsDiaRepes}</TableCell>
                                    <TableCell align="right">{connector.numMovimentsFiltratsConfig}</TableCell>
                                    <TableCell align="right">{connector.numMovimentsConcepteAssignat}</TableCell>
                                </TableRow>
                            ))
                          ))}
                      </TableBody>
                  </Table>
              </TableContainer>
          )}

          {/* Mensaje si no hay datos para la fecha */}
          {!loadingResults && !errorResults && (!syncResult || syncResult.length === 0) && (
               <Typography sx={{ marginTop: 2, textAlign: 'center', fontStyle: 'italic' }}>
                   No hi ha dades de sincronització per la data seleccionada ({selectedDate}).
               </Typography>
           )}
      </Paper>
    </Box>
  );
};

export default SincroBancaria;
